// Contract History (activity log) display helpers.
export const HISTORY_TYPE_SEVERITY: Record<
  string,
  "success" | "info" | "warning" | "danger"
> = {
  Created: "success",
  Updated: "info",
  ProceduresRegenerated: "warning",
  AttachmentAdded: "success",
  AttachmentRemoved: "danger",
  CarveoutAdded: "success",
  CarveoutRemoved: "danger",
};

export const HISTORY_TYPE_LABELS: Record<string, string> = {
  Created: "Created",
  Updated: "Updated",
  ProceduresRegenerated: "Procedures Regenerated",
  AttachmentAdded: "Attachment Added",
  AttachmentRemoved: "Attachment Removed",
  CarveoutAdded: "Carve-out Added",
  CarveoutRemoved: "Carve-out Removed",
  ProceduresGenerated: "Procedures Generated",
};

export const HISTORY_FIELD_LABELS: Record<string, string> = {
  providerId: "Provider",
  payerId: "Payer",
  effectiveDate: "Effective Date",
  nextUpdateDate: "Next Update Date",
  updateTerm: "Update Term",
  updatePercent: "Percent Increase",
  notes: "Notes",
  implantTerms: "Implant Terms",
  // Legacy read-only (no longer produced after CLE-2525) — kept so old audit rows still render.
  mpdrRulesetIds: "MPPR Rulesets",
  caseRateRulesetIds: "Case Rate Rulesets",
  explodingFeeScheduleRulesetIds: "Exploding Fee Schedule Rulesets",
  packageRulesetIds: "Package Rulesets",
  inpatientRulesetIds: "Inpatient Rulesets",
  // CLE-2751: also carries linked Medicaid payers, so the history label is the generic one.
  medicareAdvantagePayerIds: "Linked Payers",
  // CLE-2786: providers sharing this contract instead of holding a duplicate of it.
  linkedProviderIds: "Connected Providers",
  rateCalculations: "Rate Calculations",
  baseRateFileKind: "File Type",
  carveoutRate: "Carve-out Rate",
};

export type ContractUpdateTerm = "MedicareAnnual" | "PercentIncrease";

export interface IUpdateTerms {
  updateTerm: ContractUpdateTerm | null;
  updatePercent: number | null;
}

/**
 * CLE-2781 — coerce the update-term pair into one of the three shapes the API accepts, mirroring
 * `assertValidUpdateTerms` on the backend:
 *
 *   no next update date  → no term, no percent
 *   MedicareAnnual       → no percent
 *   PercentIncrease      → the percent as entered (0 included — a real negotiated outcome)
 *
 * The form calls this on every change AND again at submit, because the two ways to leave a stale
 * value behind are switching PercentIncrease → MedicareAnnual and clearing the date after a term
 * was chosen. Either one would otherwise send the API a body it rejects.
 */
export const normalizeUpdateTerms = (
  nextUpdateDate: Date | null | undefined,
  terms: Partial<IUpdateTerms>
): IUpdateTerms => {
  if (!nextUpdateDate) return { updateTerm: null, updatePercent: null };
  if (terms.updateTerm !== "PercentIncrease") {
    return { updateTerm: terms.updateTerm ?? null, updatePercent: null };
  }
  return {
    updateTerm: "PercentIncrease",
    updatePercent: terms.updatePercent ?? null,
  };
};

/**
 * CLE-2781 — how a contract's rates move at the next update date. The dropdown options and the
 * display labels come from the same place so the list column and the form cannot drift apart.
 */
export const UPDATE_TERM_LABELS: Record<string, string> = {
  MedicareAnnual: "Medicare Annual",
  PercentIncrease: "Percent Increase",
};

export const UPDATE_TERM_OPTIONS = Object.entries(UPDATE_TERM_LABELS).map(
  ([value, label]) => ({ label, value })
);

/**
 * Render an update term for display: `Medicare Annual`, or `Increase: 3%` (CLE-2788 — Ani
 * asked for the short form wherever the number is shown; the picker still says "Percent Increase").
 *
 * Returns null when there is no term, so callers can render their own empty state (the list
 * view shows a dash, the detail view shows "Not set") rather than a hardcoded string.
 */
export const formatUpdateTerm = (
  updateTerm: string | null | undefined,
  updatePercent: number | string | null | undefined
): string | null => {
  if (!updateTerm) return null;
  const label = UPDATE_TERM_LABELS[updateTerm] ?? updateTerm;
  if (updateTerm !== "PercentIncrease") return label;
  // 0 is a real negotiated outcome, so test for null/undefined rather than falsiness. The API
  // sends a Prisma Decimal, which arrives as a string.
  if (
    updatePercent === null ||
    updatePercent === undefined ||
    updatePercent === ""
  ) {
    return label;
  }
  const pct = Number(updatePercent);
  if (Number.isNaN(pct)) return label;
  // Trim a trailing `.00` so `3.00` from the database reads as `3%`, but keep `3.5%`.
  return `Increase: ${Number(pct.toFixed(2))}%`;
};

/** Render a `{ ratePercent, rateType }` pair as `$12.50/unit` or `85%`. */
export const formatRate = (
  ratePercent: number | null | undefined,
  rateType: unknown
): string => {
  if (ratePercent === null || ratePercent === undefined) return "Not set";
  const value = Number(ratePercent);
  return rateType === "unit"
    ? `$${(value / 100).toFixed(2)}/unit`
    : `${value}%`;
};

/**
 * Render a history `from`/`to` value as readable text (handles provider/payer name objects,
 * arrays, rate calcs). Pass `field` for values that only read well with their field's context —
 * CLE-2781's `updateTerm` stores the enum key (`PercentIncrease`) and `updatePercent` a bare
 * number, neither of which an analyst should see raw.
 */
export const formatHistoryValue = (value: unknown, field?: string): string => {
  if (value === null || value === undefined || value === "") return "Not set";
  if (field === "updateTerm" && typeof value === "string") {
    return UPDATE_TERM_LABELS[value] ?? value;
  }
  if (field === "updatePercent") {
    const pct = Number(value);
    return Number.isNaN(pct) ? String(value) : `${Number(pct.toFixed(2))}%`;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return "None";
    return value
      .map((v) => {
        if (v && typeof v === "object") {
          const o = v as any;
          if (o.name) return o.name as string;
          if (o.ratePercent !== undefined) {
            return formatRate(o.ratePercent, o.rateType);
          }
          // Id-bearing object whose name didn't resolve (e.g. deleted ruleset).
          if (o.id !== undefined) return "Unnamed";
          return JSON.stringify(o);
        }
        return String(v);
      })
      .join(", ");
  }
  if (typeof value === "object") {
    const o = value as any;
    if (o.name) return o.name as string;
    // Carve-out rate edits (CLE-2573) log a bare { ratePercent, rateType } pair.
    if (o.ratePercent !== undefined)
      return formatRate(o.ratePercent, o.rateType);
    if (o.id !== undefined) return "Unnamed";
    return JSON.stringify(o);
  }
  return String(value);
};

/**
 * Readable label for a single rate calculation: base rate file name + its rate.
 * The name comes from the history snapshot itself (`displayName`), which now
 * carries the base rate file name alongside the ids.
 */
const formatRateCalculationLabel = (calc: any): string => {
  const name =
    calc?.displayName?.trim() ||
    calc?.baseRateFile?.displayName?.trim() ||
    "Unnamed";
  if (calc?.ratePercent === undefined || calc?.ratePercent === null) {
    return name;
  }
  const ratePercent = Number(calc.ratePercent);
  const rate =
    calc.rateType === "unit"
      ? `$${(ratePercent / 100).toFixed(2)}/unit`
      : `${ratePercent}%`;
  return `${name} ${rate}`;
};

/**
 * Diff two rate-calculation array snapshots (history `from`/`to`), identified by
 * base rate file. Names come from each snapshot's `displayName`.
 *
 * `changed` covers files that stayed attached but had their rate corrected
 * (CLE-2573) — without it an in-place rate edit reads as "No changes".
 */
export const diffRateCalculations = (
  from: unknown,
  to: unknown
): { added: string[]; removed: string[]; changed: string[] } => {
  const fromArr = Array.isArray(from) ? (from as any[]) : [];
  const toArr = Array.isArray(to) ? (to as any[]) : [];
  const keyOf = (calc: any): string => calc?.baseRateFileId;
  const fromKeys = new Set(fromArr.map(keyOf));
  const toByKey = new Map(toArr.map((calc) => [keyOf(calc), calc] as const));
  const sameRate = (a: any, b: any): boolean =>
    Number(a?.ratePercent) === Number(b?.ratePercent) &&
    (a?.rateType ?? "percent") === (b?.rateType ?? "percent");

  return {
    added: toArr
      .filter((calc) => !fromKeys.has(keyOf(calc)))
      .map(formatRateCalculationLabel),
    removed: fromArr
      .filter((calc) => !toByKey.has(keyOf(calc)))
      .map(formatRateCalculationLabel),
    changed: fromArr
      .filter((calc) => {
        const next = toByKey.get(keyOf(calc));
        return next !== undefined && !sameRate(calc, next);
      })
      .map((calc) => {
        const next = toByKey.get(keyOf(calc));
        return `${formatRateCalculationLabel(calc)} → ${formatRate(
          next?.ratePercent,
          next?.rateType
        )}`;
      }),
  };
};

/** History fields that are named lists (e.g. rulesets) — shown as added/removed diffs. */
export const HISTORY_LIST_FIELDS = new Set([
  // The five *RulesetIds entries are legacy read-only (no longer produced after CLE-2525).
  "mpdrRulesetIds",
  "caseRateRulesetIds",
  "explodingFeeScheduleRulesetIds",
  "packageRulesetIds",
  "inpatientRulesetIds",
  "medicareAdvantagePayerIds",
  "linkedProviderIds",
]);

/**
 * Diff two snapshots of a named list (history `from`/`to`) into added/removed
 * label lists. Items are identified by `id` (falling back to their name/value)
 * and labelled by `name`.
 */
export const diffNamedList = (
  from: unknown,
  to: unknown
): { added: string[]; removed: string[] } => {
  const fromArr = Array.isArray(from) ? (from as any[]) : [];
  const toArr = Array.isArray(to) ? (to as any[]) : [];
  const keyOf = (item: any): string =>
    item && typeof item === "object"
      ? item.id ?? item.name ?? JSON.stringify(item)
      : String(item);
  const labelOf = (item: any): string => {
    if (!item || typeof item !== "object") return String(item);
    const name = item.name ?? "Unnamed";
    if (item.providerName || item.payerName) {
      return `${name} (${item.providerName ?? ""}-${item.payerName ?? ""})`;
    }
    return name;
  };
  const fromKeys = new Set(fromArr.map(keyOf));
  const toKeys = new Set(toArr.map(keyOf));
  return {
    added: toArr.filter((item) => !fromKeys.has(keyOf(item))).map(labelOf),
    removed: fromArr.filter((item) => !toKeys.has(keyOf(item))).map(labelOf),
  };
};
